import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GraduationCap,
  Plus,
  Power,
  PowerOff,
  Filter,
  ScrollText,
} from "lucide-react";

import { ApiError, formatCLP, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  cn,
  controlClasses,
  type BadgeTone,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarSubsidio,
  crearSubsidio,
  listarAplicadosPeriodo,
  listarOrganismos,
  listarPorContrato,
  type OrganismoRead,
  type SubsidioCreatePayload,
  type SubsidioRead,
} from "./api";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { MESES } from "../cierres/estado";

const HOY = new Date();

export function SubsidiosListPage(): JSX.Element {
  const [contratoId, setContratoId] = useState<string>("");
  const [openForm, setOpenForm] = useState(false);

  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  const subsidiosQ = useQuery({
    queryKey: ["subsidios-contrato", contratoId],
    queryFn: () => listarPorContrato(Number(contratoId)),
    enabled: !!contratoId,
  });

  const organismosQ = useQuery({
    queryKey: ["organismos", false],
    queryFn: () => listarOrganismos(false),
  });

  const contratoSeleccionado = useMemo(
    () => contratosQ.data?.find((c) => String(c.id) === contratoId) ?? null,
    [contratosQ.data, contratoId],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Subsidios"
        icon={GraduationCap}
        subtitle="Decretos de subsidio FNDR por contrato y su aplicación en cada periodo."
        actions={
          <Button
            icon={Plus}
            onClick={() => setOpenForm((v) => !v)}
            disabled={!contratosQ.data}
          >
            Registrar decreto
          </Button>
        }
      />

      {openForm && (
        <CrearSubsidioForm
          contratos={contratosQ.data ?? []}
          organismos={organismosQ.data ?? []}
          contratoIdHint={contratoId ? Number(contratoId) : null}
          onClose={() => setOpenForm(false)}
        />
      )}

      <Card padding="sm">
        <div className="flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={contratoId}
            onChange={(e) => setContratoId(e.target.value)}
            aria-label="Elegir contrato"
            className={cn(controlClasses, "w-auto")}
          >
            <option value="">— elegir contrato —</option>
            {(contratosQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.socio_nombre} · {c.arranque_codigo ?? "s/cód"}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {!contratoId && (
        <p className="text-sm text-neutral-500">
          Elige un contrato para ver su histórico de decretos. Cada contrato
          tiene su propio subsidio.
        </p>
      )}

      {contratoId && contratoSeleccionado && (
        <div className="text-sm text-neutral-600">
          Mostrando subsidios de{" "}
          <strong>{contratoSeleccionado.socio_nombre}</strong> en arranque{" "}
          {contratoSeleccionado.arranque_codigo ?? "—"}.
        </div>
      )}

      {subsidiosQ.isPending && contratoId && <Cargando />}
      {subsidiosQ.isError && (
        <ErrorCarga
          error={subsidiosQ.error}
          mensaje="Error al cargar los subsidios del contrato."
          onReintentar={() => void subsidiosQ.refetch()}
        />
      )}

      {subsidiosQ.data && subsidiosQ.data.length === 0 && (
        <p className="text-sm text-neutral-500">Sin decretos registrados.</p>
      )}

      {subsidiosQ.data && subsidiosQ.data.length > 0 && (
        <TableWrap label="Decretos de subsidio del contrato">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Decreto</th>
                <th scope="col" className="px-3 py-2 text-left">Organismo</th>
                <th scope="col" className="px-3 py-2 text-left">Vigencia</th>
                <th scope="col" className="px-3 py-2 text-right">Porcentaje</th>
                <th scope="col" className="px-3 py-2 text-right">Tope m³</th>
                <th scope="col" className="px-3 py-2 text-right">Tope $</th>
                <th scope="col" className="px-3 py-2 text-left">Activo</th>
                <th scope="col" className="px-3 py-2 text-right">{""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {subsidiosQ.data.map((s) => (
                <SubsidioRow
                  key={s.id}
                  subsidio={s}
                  organismos={organismosQ.data ?? []}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      <AplicadosPeriodoSection />
    </div>
  );
}

function SubsidioRow({
  subsidio,
  organismos,
}: {
  subsidio: SubsidioRead;
  organismos: OrganismoRead[];
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: () =>
      actualizarSubsidio(subsidio.id, { activo: !subsidio.activo }),
    onSuccess: (s) => {
      toast.success(s.activo ? "Subsidio reactivado" : "Subsidio desactivado");
      void qc.invalidateQueries({
        queryKey: ["subsidios-contrato", String(subsidio.contrato_id)],
      });
      void qc.invalidateQueries({ queryKey: ["subsidio-vigente"] });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const cambiarOrganismo = useMutation({
    mutationFn: (organismoId: number | null) =>
      actualizarSubsidio(subsidio.id, { organismo_id: organismoId }),
    onSuccess: () => {
      toast.success("Organismo actualizado");
      void qc.invalidateQueries({
        queryKey: ["subsidios-contrato", String(subsidio.contrato_id)],
      });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo asignar organismo";
      toast.error(msg);
    },
  });

  const vencido =
    new Date(subsidio.fecha_vencimiento) < new Date(new Date().toDateString());

  return (
    <tr className={vencido ? "opacity-60" : "hover:bg-neutral-50"}>
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">
          {subsidio.numero_decreto}
        </div>
        {subsidio.fecha_decreto && (
          <div className="text-xs text-neutral-500">
            Decreto del {formatDate(subsidio.fecha_decreto)}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <select
          value={subsidio.organismo_id ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            cambiarOrganismo.mutate(v === "" ? null : Number(v));
          }}
          disabled={cambiarOrganismo.isPending}
          aria-label="Organismo del subsidio"
          className={cn(controlClasses, "w-auto py-1 text-xs")}
        >
          <option value="">— sin asignar —</option>
          {organismos.map((o) => (
            <option key={o.id} value={o.id}>
              {o.codigo} · {o.nombre}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <div>{formatDate(subsidio.fecha_inicio)}</div>
        <div className="text-xs text-neutral-500">
          → {formatDate(subsidio.fecha_vencimiento)}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-medium tabular-nums">
        {subsidio.porcentaje_subsidio}%
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{subsidio.tope_m3} m³</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {subsidio.tope_monetario != null
          ? formatCLP(subsidio.tope_monetario)
          : "—"}
      </td>
      <td className="px-3 py-2">
        {subsidio.activo ? (
          <Badge tone="emerald">
            <Power className="h-3 w-3" strokeWidth={1.5} />
            Activo
          </Badge>
        ) : (
          <Badge tone="slate">
            <PowerOff className="h-3 w-3" strokeWidth={1.5} />
            Inactivo
          </Badge>
        )}
        {vencido && <div className="mt-0.5 text-xs text-rose-700">vencido</div>}
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            setError(null);
            toggle.mutate();
          }}
          disabled={toggle.isPending}
        >
          {toggle.isPending
            ? "…"
            : subsidio.activo
              ? "Desactivar"
              : "Reactivar"}
        </Button>
        {error && <p className="text-xs text-rose-700">{error}</p>}
      </td>
    </tr>
  );
}

interface FormProps {
  contratos: ContratoOperativo[];
  organismos: OrganismoRead[];
  contratoIdHint: number | null;
  onClose: () => void;
}

function CrearSubsidioForm({
  contratos,
  organismos,
  contratoIdHint,
  onClose,
}: FormProps): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [contratoId, setContratoId] = useState<string>(
    contratoIdHint ? String(contratoIdHint) : "",
  );
  const [organismoId, setOrganismoId] = useState<string>("");
  const [numeroDecreto, setNumeroDecreto] = useState("");
  const [fechaDecreto, setFechaDecreto] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [porcentaje, setPorcentaje] = useState<string>("50");
  const [topeM3, setTopeM3] = useState<string>("15");
  const [topeMonetario, setTopeMonetario] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: SubsidioCreatePayload) => crearSubsidio(p),
    onSuccess: (s) => {
      toast.success(`Decreto ${s.numero_decreto} registrado`);
      void qc.invalidateQueries({
        queryKey: ["subsidios-contrato", String(s.contrato_id)],
      });
      void qc.invalidateQueries({ queryKey: ["subsidio-vigente"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!contratoId) return setError("Elige un contrato");
    if (!numeroDecreto.trim()) return setError("Número de decreto requerido");
    if (!fechaInicio || !fechaVencimiento)
      return setError("Vigencia inicio y vencimiento requeridos");
    const p = Number(porcentaje);
    if (!Number.isFinite(p) || p < 1 || p > 100)
      return setError("Porcentaje 1-100");
    const t = Number(topeM3);
    if (!Number.isFinite(t) || t < 0) return setError("Tope m³ inválido");
    const tm = topeMonetario.trim() ? Number(topeMonetario) : null;
    if (tm !== null && (!Number.isFinite(tm) || tm < 0))
      return setError("Tope monetario inválido");
    mutation.mutate({
      contrato_id: Number(contratoId),
      numero_decreto: numeroDecreto.trim(),
      fecha_decreto: fechaDecreto || null,
      fecha_inicio: fechaInicio,
      fecha_vencimiento: fechaVencimiento,
      porcentaje_subsidio: p,
      tope_m3: t,
      tope_monetario: tm,
      activo: true,
      organismo_id: organismoId ? Number(organismoId) : null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    contratoId, organismoId, numeroDecreto, fechaDecreto, fechaInicio,
    fechaVencimiento, porcentaje, topeM3, topeMonetario,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Registrar decreto de subsidio"
      icon={GraduationCap}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Creando…" : "Crear decreto"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Contrato"
            value={contratoId}
            onChange={(e) => setContratoId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— elegir —</option>
            {contratos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.socio_nombre} · {c.arranque_codigo ?? "s/cód"}
              </option>
            ))}
          </Select>
          <Input
            label="Número decreto"
            type="text"
            maxLength={50}
            value={numeroDecreto}
            onChange={(e) => setNumeroDecreto(e.target.value)}
            placeholder="ej. DS-1234/2026"
            disabled={mutation.isPending}
          />
          <Input
            label="Fecha del decreto"
            type="date"
            value={fechaDecreto}
            onChange={(e) => setFechaDecreto(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>

        <Select
          label="Organismo subsidiador"
          hint="Quién financia este decreto. Necesario para emitir la rendición mensual al organismo correspondiente."
          value={organismoId}
          onChange={(e) => setOrganismoId(e.target.value)}
          disabled={mutation.isPending}
        >
          <option value="">— sin asignar —</option>
          {organismos.map((o) => (
            <option key={o.id} value={o.id}>
              {o.codigo} · {o.nombre}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Vigencia desde"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            disabled={mutation.isPending}
          />
          <Input
            label="Vigencia hasta"
            type="date"
            value={fechaVencimiento}
            onChange={(e) => setFechaVencimiento(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            label="Porcentaje (1-100)"
            type="number"
            min={1}
            max={100}
            value={porcentaje}
            onChange={(e) => setPorcentaje(e.target.value)}
            disabled={mutation.isPending}
          />
          <Input
            label="Tope m³ subsidiables"
            type="number"
            min={0}
            value={topeM3}
            onChange={(e) => setTopeM3(e.target.value)}
            disabled={mutation.isPending}
          />
          <Input
            label="Tope monetario CLP (opcional)"
            type="number"
            min={0}
            value={topeMonetario}
            onChange={(e) => setTopeMonetario(e.target.value)}
            placeholder="sin tope"
            disabled={mutation.isPending}
          />
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

const FNDR_TONE: Record<
  "Aplicado" | "Cobrado_FNDR" | "Rechazado_FNDR",
  BadgeTone
> = {
  Aplicado: "amber",
  Cobrado_FNDR: "emerald",
  Rechazado_FNDR: "rose",
};
const FNDR_LABEL: Record<
  "Aplicado" | "Cobrado_FNDR" | "Rechazado_FNDR",
  string
> = {
  Aplicado: "Aplicado",
  Cobrado_FNDR: "Cobrado FNDR",
  Rechazado_FNDR: "Rechazado FNDR",
};

function EstadoFNDRBadge({
  estado,
}: {
  estado: "Aplicado" | "Cobrado_FNDR" | "Rechazado_FNDR";
}): JSX.Element {
  return <Badge tone={FNDR_TONE[estado]}>{FNDR_LABEL[estado]}</Badge>;
}

function AplicadosPeriodoSection(): JSX.Element {
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [anio, setAnio] = useState(HOY.getFullYear());

  const aplicadosQ = useQuery({
    queryKey: ["subsidios-aplicados", mes, anio],
    queryFn: () => listarAplicadosPeriodo(mes, anio),
  });

  const totalAplicado = (aplicadosQ.data ?? []).reduce(
    (a, x) => a + x.monto_aplicado,
    0,
  );
  const totalRecuperado = (aplicadosQ.data ?? []).reduce(
    (a, x) => a + x.monto_recuperado_fndr,
    0,
  );

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200">
      <header className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
        <ScrollText className="h-4 w-4" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-neutral-900">
          Aplicados en el periodo (informe FNDR)
        </h3>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <select
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
            aria-label="Mes"
            className={cn(controlClasses, "w-auto py-1")}
          >
            {MESES.map((nombre, idx) => (
              <option key={idx} value={idx + 1}>
                {nombre}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={2020}
            max={2100}
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            aria-label="Año"
            className={cn(controlClasses, "w-24 py-1")}
          />
        </div>
      </header>
      <div className="p-4">
        {aplicadosQ.isPending ? (
          <Cargando inline />
        ) : aplicadosQ.isError ? (
          <ErrorCarga
            error={aplicadosQ.error}
            onReintentar={() => void aplicadosQ.refetch()}
          />
        ) : (aplicadosQ.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">
            Sin subsidios aplicados en {MESES[mes - 1]} {anio}.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3">
                <div className="text-xs uppercase tracking-wider text-emerald-800">
                  Total aplicado
                </div>
                <div className="mt-1 text-lg font-semibold text-emerald-900 tabular-nums">
                  {formatCLP(totalAplicado)}
                </div>
              </div>
              <div className="rounded-md border border-brand-100 bg-brand-50 p-3">
                <div className="text-xs uppercase tracking-wider text-brand-800">
                  Recuperado FNDR
                </div>
                <div className="mt-1 text-lg font-semibold text-brand-900 tabular-nums">
                  {formatCLP(totalRecuperado)}
                </div>
              </div>
            </div>
            <TableWrap label="Subsidios aplicados en el periodo">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Subsidio</th>
                    <th scope="col" className="px-3 py-2 text-left">Lectura</th>
                    <th scope="col" className="px-3 py-2 text-right">m³ subsidiados</th>
                    <th scope="col" className="px-3 py-2 text-right">Aplicado</th>
                    <th scope="col" className="px-3 py-2 text-right">Recuperado</th>
                    <th scope="col" className="px-3 py-2 text-left">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {aplicadosQ.data!.map((a) => (
                    <tr key={a.id}>
                      <td className="px-3 py-2 font-mono text-xs">
                        #{a.subsidio_id}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        #{a.lectura_id}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {a.m3_subsidiados} / {a.tope_m3_periodo}
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">
                        {formatCLP(a.monto_aplicado)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatCLP(a.monto_recuperado_fndr)}
                      </td>
                      <td className="px-3 py-2">
                        <EstadoFNDRBadge estado={a.estado} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </div>
        )}
      </div>
    </section>
  );
}
